import { useEffect, useRef, useState, useMemo, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  FaRegFileAlt, FaUser, FaRegCheckSquare, FaRegEnvelope, 
  FaHome, FaCalendarAlt, FaCog, FaArchive, FaPlus, FaMoon, FaSignOutAlt, FaSun
} from 'react-icons/fa'
import { FiSearch, FiLoader } from 'react-icons/fi'
import { BsChatDots } from 'react-icons/bs'
import { MdQuestionMark } from 'react-icons/md'
import { searchCrm } from '../api/crm'
import { logoutSession } from '../utils/sessionAuth'
import { getSessionUserId, clearSessionUserId } from '../utils/sessionUser'
import '../style/components/CommandPalette.css'
import { CiCalendar } from "react-icons/ci";

type SearchResult = {
  id: string
  type: 'CHAT' | 'CALL' | 'EML' | 'CLT' | 'AI' | 'CAL'
  title?: string
  snippet?: string
  dateCreated?: string
  tags?: string[] | string | null
}

type PaletteAction = {
  id: string
  label: string
  category: 'NAVIGATION' | 'ACTIONS' | 'SYSTEM'
  icon: ReactNode
  onExecute: () => void
}

function getResultIcon(type: string) {
  const t = type.toUpperCase()
  switch (t) {
    case 'CHAT': return <BsChatDots />
    case 'CALL': return <FaRegFileAlt />
    case 'EML': return <FaRegEnvelope />
    case 'CLT': return <FaUser />
    case 'AI': return <FaRegCheckSquare />
    case 'CAL': return <CiCalendar />
    default: return <MdQuestionMark />
  }
}

export default function CommandPalette() {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isKeyboardNav = useRef(false)

  const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark'
  
  // Settings Check - Default to false if not set
  const showCommandsByDefault = window.localStorage.getItem('searchShowCommandsByDefault') === 'true'

  const staticActions: PaletteAction[] = useMemo(() => [
    { id: 'nav-home', label: 'View Home', category: 'NAVIGATION', icon: <FaHome />, onExecute: () => { navigate('/'); setIsOpen(false); } },
    { id: 'nav-cal', label: 'View Calendar', category: 'NAVIGATION', icon: <FaCalendarAlt />, onExecute: () => { navigate('/calendar'); setIsOpen(false); } },
    { id: 'act-calendar', label: 'Add to Calendar', category: 'ACTIONS', icon: <FaPlus />, onExecute: () => { navigate('/calendar#new'); setIsOpen(false); } },
    { id: 'nav-settings', label: 'View Settings', category: 'NAVIGATION', icon: <FaCog />, onExecute: () => { navigate('/settings'); setIsOpen(false); } },
    { id: 'nav-archives', label: 'View Archived Notes', category: 'NAVIGATION', icon: <FaArchive />, onExecute: () => { navigate('/archives'); setIsOpen(false); } },
    { id: 'act-contact', label: 'Create New Contact', category: 'ACTIONS', icon: <FaPlus />, onExecute: () => { navigate('/contact/new'); setIsOpen(false); } },
    { id: 'sys-theme', label: `Switch to ${isDarkMode ? 'Light' : 'Dark'} Mode`, category: 'SYSTEM', icon: isDarkMode ? <FaSun /> : <FaMoon />, onExecute: () => {
      const next = !isDarkMode
      if (next) document.documentElement.setAttribute('data-theme', 'dark')
      else document.documentElement.removeAttribute('data-theme')
      window.localStorage.setItem('appDarkModeEnabled', next ? 'true' : 'false')
      setIsOpen(false)
    }},
    { id: 'sys-logout', label: 'Log Out', category: 'SYSTEM', icon: <FaSignOutAlt />, onExecute: () => {
      setIsOpen(false)
      logoutSession()
      clearSessionUserId()
      window.location.href = '/'
    }},
  ], [navigate, isDarkMode])

  const filteredActions = useMemo(() => {
    if (!query.trim()) {
      return showCommandsByDefault ? staticActions : []
    }
    const q = query.toLowerCase()
    const strippedQ = q.replace(/^(open|go to|view|show)\s+/i, '')
    
    return staticActions.filter(a => 
      a.label.toLowerCase().includes(q) || 
      a.label.toLowerCase().includes(strippedQ) ||
      a.category.toLowerCase().includes(q)
    )
  }, [query, staticActions, showCommandsByDefault])

  const totalItemsCount = filteredActions.length + results.length

  // Auto-scroll active item into view
  useEffect(() => {
    if (isOpen && scrollRef.current && isKeyboardNav.current) {
      const activeEl = scrollRef.current.querySelector('.cp-result-item.active') as HTMLElement
      if (activeEl) {
        const container = scrollRef.current
        const itemTop = activeEl.offsetTop
        const itemHeight = activeEl.offsetHeight

        if (itemTop < container.scrollTop) {
          container.scrollTop = itemTop - 10
        } else if (itemTop + itemHeight > container.scrollTop + container.clientHeight) {
          container.scrollTop = itemTop + itemHeight - container.clientHeight + 10
        }
      }
    }
  }, [activeIndex, isOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.code === 'Space') {
        e.preventDefault()
        setIsOpen((prev) => !prev)
      } else if (e.key === 'Escape' && isOpen) {
        setIsOpen(false)
      } else if (isOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          isKeyboardNav.current = true
          setActiveIndex(prev => (prev + 1) % totalItemsCount)
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          isKeyboardNav.current = true
          setActiveIndex(prev => (prev - 1 + totalItemsCount) % totalItemsCount)
        } else if (e.key === 'Enter') {
          e.preventDefault()
          if (activeIndex < filteredActions.length) {
            filteredActions[activeIndex].onExecute()
          } else {
            handleResultClick(results[activeIndex - filteredActions.length])
          }
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, activeIndex, totalItemsCount, filteredActions, results])

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    } else {
      setQuery('')
      setResults([])
    }
  }, [isOpen])

  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const userId = getSessionUserId()
        const data = await searchCrm(query.trim(), userId || '')
        setResults(data)
      } catch (err) {
        console.error('CP Search failed', err)
      } finally {
        setIsSearching(false)
      }
    }, 250)
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    }
  }, [query])

  const handleResultClick = (result: SearchResult) => {
    setIsOpen(false)
    const rType = result.type.toUpperCase()
    switch (rType) {
      case 'CHAT': navigate(`/assistant?id=${result.id}`); break
      case 'CALL': navigate(`/conversation/${result.id}`); break
      case 'EML': {
        const cid = result.id.startsWith('email_') ? result.id.replace('email_', 'conv_') : result.id
        navigate(`/conversation/${cid}`)
        break
      }
      case 'CLT': navigate(`/contact/${result.id}`); break
      case 'AI': navigate('/action-items'); break
      case 'CAL': navigate(`/calendar#${result.id}`); break
    }
  }

  if (!isOpen) return null

  return (
    <div className="command-palette-root" onMouseMove={() => { isKeyboardNav.current = false }}>
      <div className="command-palette-overlay" onClick={() => setIsOpen(false)} />
      <div className="command-palette-container">
        <div className="command-palette-search">
          <FiSearch className="cp-search-icon" size={20} />
          <input
            ref={inputRef}
            placeholder="Search commands or data..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {isSearching && <FiLoader className="cp-loader spin" size={18} />}
        </div>

        <div className="command-palette-body" ref={scrollRef}>
          {filteredActions.length > 0 && (
            <div className="cp-section">
              <div className="cp-section-header">Commands</div>
              {filteredActions.map((action, i) => (
                <button
                  type="button"
                  key={action.id}
                  className={`cp-result-item action-item ${activeIndex === i ? 'active' : ''}`}
                  onClick={action.onExecute}
                  onMouseEnter={() => {
                    if (!isKeyboardNav.current) setActiveIndex(i)
                  }}
                >
                  <div className="cp-result-icon action">{action.icon}</div>
                  <div className="cp-result-info">
                    <div className="cp-result-title">{action.label}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results.length > 0 && (
            <div className="cp-section">
              <div className="cp-section-header">Results</div>
              {results.map((result, i) => {
                const globalIndex = i + filteredActions.length
                const tag = Array.isArray(result.tags) ? result.tags[0] : result.tags
                const rType = result.type.toUpperCase()
                const isEvent = rType === 'CAL'
                
                return (
                  <button
                    type="button"
                    key={`${result.type}-${result.id}`}
                    className={`cp-result-item ${activeIndex === globalIndex ? 'active' : ''}`}
                    onClick={() => handleResultClick(result)}
                    onMouseEnter={() => {
                      if (!isKeyboardNav.current) setActiveIndex(globalIndex)
                    }}
                  >
                    <div className={`cp-result-icon ${result.type.toLowerCase()} ${isEvent ? 'cal' : ''}`}>
                      {getResultIcon(result.type)}
                    </div>
                    <div className="cp-result-info">
                      <div className="cp-result-title">
                        {result.title}
                        {isEvent && tag && (
                          <span className={`cp-tag-badge ${tag.toLowerCase()}`}>{tag}</span>
                        )}
                      </div>
                      <div className="cp-result-snippet">
                        {result.snippet && result.snippet !== 'null' ? result.snippet : ''}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {!isSearching && query && totalItemsCount === 0 && (
            <div className="cp-empty">No results found for "{query}"</div>
          )}
        </div>
        
        <div className="command-palette-footer">
          <div className="cp-hint"><kbd>↑↓</kbd> to navigate</div>
          <div className="cp-hint"><kbd>↵</kbd> to select</div>
          <div className="cp-hint"><kbd>ESC</kbd> to close</div>
        </div>
      </div>
    </div>
  )
}
